import React from "react";
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap";
import {
  FaHome,
  FaThermometerHalf,
  FaSnowflake,
  FaTools,
  FaCog,
  FaShieldAlt,
  FaCheckCircle,
  FaWrench,
  FaClock,
  FaLeaf,
  FaMobile,
  FaWifi,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const Residential = () => {
  const residentialServices = [
    {
      icon: <FaHome className="icon-large" />,
      title: "Single-Family Homes",
      description:
        "Complete AC solutions for individual homes with personalized comfort control.",
      features: [
        "Central air systems",
        "Ductless mini-splits",
        "Smart thermostats",
        "Energy efficiency",
      ],
    },
    {
      icon: <FaThermometerHalf className="icon-large" />,
      title: "Multi-Zone Systems",
      description:
        "Advanced climate control allowing different temperatures in different rooms.",
      features: [
        "Individual room control",
        "Energy savings",
        "Comfort optimization",
        "Smart zoning",
      ],
    },
    {
      icon: <FaSnowflake className="icon-large" />,
      title: "AC Installation",
      description:
        "Professional installation of new air conditioning systems for your home.",
      features: [
        "Expert installation",
        "Warranty coverage",
        "Post-installation support",
        "Energy optimization",
      ],
    },
    {
      icon: <FaTools className="icon-large" />,
      title: "AC Repair",
      description:
        "Fast and reliable repair services for all residential AC systems.",
      features: [
        "24/7 emergency service",
        "Diagnostic testing",
        "Parts replacement",
        "Performance tuning",
      ],
    },
    {
      icon: <FaCog className="icon-large" />,
      title: "Maintenance Plans",
      description:
        "Regular maintenance to keep your AC running efficiently and extend its lifespan.",
      features: [
        "Seasonal tune-ups",
        "Filter replacement",
        "System cleaning",
        "Performance checks",
      ],
    },
    {
      icon: <FaShieldAlt className="icon-large" />,
      title: "Warranty Service",
      description:
        "Comprehensive warranty coverage and service for your peace of mind.",
      features: [
        "Extended warranties",
        "Parts coverage",
        "Labor included",
        "Priority service",
      ],
    },
  ];

  const benefits = [
    "Lower energy bills",
    "Improved home comfort",
    "Better indoor air quality",
    "Increased home value",
    "Extended system lifespan",
    "Smart home integration",
  ];

  const smartFeatures = [
    {
      icon: <FaMobile className="text-primary" />,
      title: "Mobile Control",
      description: "Control your AC from anywhere using your smartphone",
    },
    {
      icon: <FaWifi className="text-primary" />,
      title: "WiFi Connectivity",
      description: "Smart thermostats with WiFi for remote monitoring",
    },
    {
      icon: <FaLeaf className="text-primary" />,
      title: "Energy Savings",
      description: "AI-powered optimization to reduce energy consumption",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Residential AC Service Lahore | Home AC Installation & Repair
        </title>
        <meta
          name="description"
          content="Residential AC service in Lahore & Punjab. Expert home AC installation, repair, maintenance, and smart solutions. ای سی سروس گھر کے لیے لاہور۔"
        />
        <meta
          name="keywords"
          content="Residential AC Service Lahore, Home AC Installation Lahore, Home AC Repair Lahore, ای سی سروس گھر لاہور"
        />
        <meta
          property="og:title"
          content="Residential AC Service Lahore | Home AC Installation & Repair"
        />
        <meta
          property="og:description"
          content="Residential AC service in Lahore & Punjab. Expert home AC installation, repair, maintenance, and smart solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://procoolac.com/residential" />
        <meta
          property="og:image"
          content="https://procoolac.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Residential AC Service Lahore | Home AC Installation & Repair"
        />
        <meta
          name="twitter:description"
          content="Residential AC service in Lahore & Punjab. Expert home AC installation, repair, maintenance, and smart solutions."
        />
        <meta
          name="twitter:image"
          content="https://procoolac.com/og-image.jpg"
        />
      </Helmet>
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <Badge bg="success" className="mb-3 px-3 py-2 fs-6">
                Residential Solutions
              </Badge>
              <h2 className="display-5 fw-bold mb-3 text-gradient">
                Home Comfort Excellence
              </h2>
              <p className="lead text-muted">
                Transform your home into a comfortable oasis with our
                comprehensive residential air conditioning services. From
                installation to maintenance, we ensure your family stays cool
                and comfortable year-round.
              </p>
            </Col>
          </Row>

          {/* Residential Services Grid */}
          <Row className="g-4 mb-5">
            {residentialServices.map((service, index) => (
              <Col lg={4} md={6} key={index}>
                <Card className="h-100 border-0 shadow-sm card-hover">
                  <Card.Body className="p-4">
                    <div className="text-center mb-3">{service.icon}</div>
                    <h4 className="fw-bold mb-3 text-center">
                      {service.title}
                    </h4>
                    <p className="text-muted mb-4 text-center">
                      {service.description}
                    </p>

                    <ul className="list-unstyled">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="mb-2">
                          <FaCheckCircle className="text-success me-2" />
                          <small>{feature}</small>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Smart Features Section */}
          <Row className="mb-5">
            <Col lg={4} className="mb-4">
              <div className="bg-success text-white rounded-4 p-5 h-100">
                <h3 className="fw-bold mb-4">Home Benefits</h3>
                <div className="row g-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="col-6">
                      <div className="d-flex align-items-center">
                        <FaCheckCircle className="text-warning me-2" />
                        <span className="small">{benefit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            <Col lg={8} className="mb-4">
              <div className="bg-white border rounded-4 p-5 h-100 shadow-sm">
                <h3 className="fw-bold mb-4 text-success">
                  Smart Home Integration
                </h3>
                <Row className="g-4">
                  {smartFeatures.map((feature, index) => (
                    <Col md={4} key={index}>
                      <div className="text-center">
                        <div className="mb-3">{feature.icon}</div>
                        <h6 className="fw-bold mb-2">{feature.title}</h6>
                        <p className="small text-muted mb-0">
                          {feature.description}
                        </p>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>

          {/* Why Choose Us Section */}
          <Row className="mb-5">
            <Col lg={6} className="mb-4">
              <div className="bg-white border rounded-4 p-5 h-100 shadow-sm">
                <h3 className="fw-bold mb-4 text-success">
                  Why Choose Our Residential Services?
                </h3>

                <div className="d-flex align-items-start mb-3">
                  <FaThermometerHalf className="text-success me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Family Comfort</h6>
                    <p className="small text-muted mb-0">
                      Ensuring your family's comfort is our priority
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                  <FaCog className="text-success me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Energy Efficiency</h6>
                    <p className="small text-muted mb-0">
                      Save money with energy-efficient solutions
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                  <FaShieldAlt className="text-success me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Reliable Service</h6>
                    <p className="small text-muted mb-0">
                      Trusted service you can count on
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start">
                  <FaClock className="text-success me-3 mt-1" />
                  <div>
                    <h6 className="fw-bold mb-1">Convenient Scheduling</h6>
                    <p className="small text-muted mb-0">
                      Flexible appointment times to fit your schedule
                    </p>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6} className="mb-4">
              <div className="bg-light rounded-4 p-5 h-100">
                <h3 className="fw-bold mb-4">Get Started Today</h3>
                <p className="mb-4">
                  Ready to upgrade your home's comfort? Contact us for a free
                  consultation and quote on your residential air conditioning
                  needs.
                </p>

                <div className="d-grid gap-3">
                  <Button
                    variant="success"
                    size="lg"
                    className="fw-semibold"
                    style={{ borderRadius: "25px" }}
                  >
                    <FaWrench className="me-2" />
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outline-success"
                    size="lg"
                    className="fw-semibold"
                    style={{ borderRadius: "25px" }}
                  >
                    <FaClock className="me-2" />
                    Schedule Service
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Call to Action */}
          <Row>
            <Col className="text-center">
              <div className="gradient-bg text-white rounded-4 p-5">
                <h3 className="fw-bold mb-3">
                  Transform Your Home Comfort Today
                </h3>
                <p className="lead mb-4">
                  Experience the difference professional residential HVAC
                  services can make in your home's comfort and energy
                  efficiency.
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  <Button
                    variant="warning"
                    size="lg"
                    className="fw-semibold px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    <FaWrench className="me-2" />
                    Get Residential Quote
                  </Button>
                  <Button
                    variant="outline-light"
                    size="lg"
                    className="fw-semibold px-4"
                    style={{ borderRadius: "30px" }}
                  >
                    <FaClock className="me-2" />
                    Book Consultation
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Residential;
